import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';

export const Hero = () => {
  const headingWords = ["Crafting", "thoughtful", "digital", "experiences"];
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.3,
      },
    },
  };

  const wordVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  const scrollToWork = () => {
    const workSection = document.getElementById('work');
    if (workSection) {
      workSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="top"
      className="min-h-screen flex flex-col justify-center pt-20 lg:pt-0 px-6 lg:px-12"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto w-full">
        <div className="max-w-4xl">
          {/* Overline */}
          <motion.p 
            className="font-body text-sm text-foreground-secondary tracking-wide uppercase mb-6 lg:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            UX / Product Designer
          </motion.p>

          {/* Main Heading */}
          <motion.h1 
            className="font-headline text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground-primary leading-[1.05] tracking-tight mb-8 lg:mb-10"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {headingWords.map((word, index) => (
              <motion.span
                key={index}
                variants={wordVariants}
                className="inline-block mr-[0.25em]"
              >
                {word}
              </motion.span>
            ))}
          </motion.h1>

          {/* Subheading */}
          <motion.p 
            className="font-body text-lg lg:text-xl text-foreground-secondary max-w-2xl leading-relaxed mb-12 lg:mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            I help startups and tech companies solve complex problems through 
            intuitive design, user research, and strategic product thinking.
          </motion.p>

          {/* Scroll indicator */}
          <motion.button
            onClick={scrollToWork}
            className="group flex items-center gap-3 text-foreground-secondary hover:text-foreground-primary transition-colors duration-200"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1.2 }}
            data-testid="scroll-to-work-button"
          >
            <span className="font-body text-sm">View case studies</span>
            <motion.div
              animate={{ y: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
            </motion.div>
          </motion.button>
        </div>

        {/* Decorative element */}
        <motion.div
          className="absolute top-1/4 right-12 lg:right-24 hidden lg:block"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <svg 
            width="120" 
            height="120" 
            viewBox="0 0 120 120" 
            className="animate-spin-slow"
          >
            <circle 
              cx="60" 
              cy="60" 
              r="55" 
              fill="none" 
              stroke="#E2EBE4" 
              strokeWidth="1.5"
              strokeDasharray="8 8"
            />
            <circle 
              cx="60" 
              cy="60" 
              r="40" 
              fill="none" 
              stroke="#A3C4AC" 
              strokeWidth="1.5"
              strokeDasharray="4 12"
            />
            <circle 
              cx="60" 
              cy="60" 
              r="5" 
              fill="#82A98D"
            />
          </svg>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
